import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { tenantService } from '../services/tenantService';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// Equipment resources mapping
export const equipmentResources = ['equipment'];

export const equipmentSubResourceMapping: Record<string, string> = {
  'equipment-maintenance': 'maintenance',
  'equipment-fuel': 'fuel',
  'equipment-inspections': 'inspections',
  'equipment-assignments': 'assignments',
  'equipment-incidents': 'incidents',
  'service-providers': 'service-providers',
};

export const equipmentSubResources = Object.keys(equipmentSubResourceMapping);

// Helper function to build query parameters for equipment resources
const buildEquipmentQueryParams = (resource: string, params: any) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field, order } = params.sort || { field: 'id', order: 'DESC' };

  if (equipmentSubResourceMapping[resource]) {
    // Equipment sub-resources use PageOptionsDto format
    return {
      page: page,
      perPage: perPage,
      orderByField: field,
      order: order.toLowerCase(), // asc/desc
      ...params.filter,
    };
  } else {
    // Main equipment resource uses the original format
    return {
      page: page,
      limit: perPage,
      sort: field,
      order: order.toLowerCase(),
      ...params.filter,
    };
  }
};

// Helper function to get the correct URL for equipment resources
const getEquipmentResourceUrl = (resource: string, params?: any) => {
  if (equipmentSubResourceMapping[resource]) {
    const subResource = equipmentSubResourceMapping[resource];

    // Service providers don't need equipmentId and always use the base endpoint
    if (resource === 'service-providers') {
      return `${baseUrl}/equipment/${subResource}`;
    }

    // Check various places where equipmentId might be provided for other equipment sub-resources
    if (
      params?.equipmentId &&
      params.equipmentId !== 'undefined' &&
      params.equipmentId !== 'null' &&
      params.equipmentId !== ''
    ) {
      return `${baseUrl}/equipment/${params.equipmentId}/${subResource}`;
    } else if (
      params?.target === 'equipmentId' &&
      params?.id &&
      params.id !== 'undefined' &&
      params.id !== 'null' &&
      params.id !== ''
    ) {
      return `${baseUrl}/equipment/${params.id}/${subResource}`;
    } else if (
      params?.data?.equipmentId &&
      params.data.equipmentId !== 'undefined' &&
      params.data.equipmentId !== 'null' &&
      params.data.equipmentId !== ''
    ) {
      return `${baseUrl}/equipment/${params.data.equipmentId}/${subResource}`;
    } else if (
      params?.filter?.equipmentId &&
      params.filter.equipmentId !== 'undefined' &&
      params.filter.equipmentId !== 'null' &&
      params.filter.equipmentId !== ''
    ) {
      // ReferenceManyField passes equipmentId in filter
      return `${baseUrl}/equipment/${params.filter.equipmentId}/${subResource}`;
    } else {
      // For reporting purposes (no equipmentId context), use top-level endpoints
      return `${baseUrl}/equipment/all-${subResource}`;
    }
  }
  return `${baseUrl}/${resource}`;
};

// Helper function for individual equipment sub-resource operations
const getEquipmentSubResourceUrl = (resource: string, id: number) => {
  if (equipmentSubResourceMapping[resource]) {
    const subResource = equipmentSubResourceMapping[resource];

    // Service providers use /equipment/{subResource}/{id}
    if (resource === 'service-providers') {
      return `${baseUrl}/equipment/${subResource}/${id}`;
    }

    // For individual operations, use the pattern: /equipment/{subResource}/{id}
    return `${baseUrl}/equipment/${subResource}/${id}`;
  }
  return `${baseUrl}/${resource}/${id}`;
};

// HTTP client with authentication and tenant headers
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData
  if (
    !(options.body instanceof FormData) &&
    options.method !== 'GET' &&
    options.method !== 'DELETE'
  ) {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Add tenant code header (similar to fortress-web approach)
  try {
    const tenantCode = tenantService.getCurrentTenantCode();
    options.headers.set('x-tenant-code', tenantCode);
  } catch (error) {
    throw new Error('No tenant selected. Please select a tenant to continue.');
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (error: any) {
    if (error.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

export const equipmentDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const query = buildEquipmentQueryParams(resource, params);
    const url = `${getEquipmentResourceUrl(resource, params.filter)}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (json.point && json.metadata) {
        data = json.point;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json.point || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching equipment ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = getEquipmentSubResourceUrl(resource, params.id);

    try {
      const { json } = await httpClient(url);
      return { data: json };
    } catch (error) {
      console.error(`Error fetching equipment ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    try {
      const promises = params.ids.map(id =>
        httpClient(getEquipmentSubResourceUrl(resource, id))
          .then(({ json }) => json)
          .catch(() => null)
      );

      const results = await Promise.all(promises);
      const data = results.filter(item => item !== null);
      return { data };
    } catch (error) {
      console.error(`Error fetching multiple equipment ${resource}:`, error);
      return { data: [] };
    }
  },

  getManyReference: async (resource, params) => {
    const query = buildEquipmentQueryParams(resource, params);

    let url;
    if (equipmentSubResourceMapping[resource]) {
      // For equipment sub-resources, params.id is the equipment ID
      if (params.id && params.id !== 'undefined' && params.id !== 'null' && params.id !== '') {
        url = `${baseUrl}/equipment/${params.id}/${
          equipmentSubResourceMapping[resource]
        }?${stringify(query)}`;
      } else {
        throw new Error(`Invalid equipment ID provided for ${resource}: ${params.id}`);
      }
    } else {
      // For non-equipment sub-resources, add the target parameter
      query[params.target] = params.id;
      url = `${getEquipmentResourceUrl(resource, params)}?${stringify(query)}`;
    }

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (json.point && json.metadata) {
        data = json.point;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json.point || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching equipment ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    const url = getEquipmentResourceUrl(resource, params);

    // Check if we have file uploads
    const hasFiles =
      params.data &&
      ((params.data.ownershipPermitFiles && params.data.ownershipPermitFiles.length > 0) ||
        (params.data.weightRatingFiles && params.data.weightRatingFiles.length > 0));

    try {
      const requestOptions: any = { method: 'POST' };

      if (hasFiles) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Add all regular fields to FormData
        Object.keys(params.data).forEach(key => {
          if (key !== 'ownershipPermitFiles' && key !== 'weightRatingFiles') {
            formData.append(key, params.data[key]);
          }
        });

        // Add ownership permit files
        if (params.data.ownershipPermitFiles) {
          params.data.ownershipPermitFiles.forEach((file: File) => {
            formData.append('ownershipPermitFiles', file);
          });
        }

        // Add weight rating files
        if (params.data.weightRatingFiles) {
          params.data.weightRatingFiles.forEach((file: File) => {
            formData.append('weightRatingFiles', file);
          });
        }

        requestOptions.body = formData;
      } else {
        // Use JSON for regular data
        requestOptions.body = JSON.stringify(params.data);
      }

      const { json } = await httpClient(url, requestOptions);

      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating equipment ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const url = equipmentSubResourceMapping[resource]
      ? getEquipmentSubResourceUrl(resource, params.id)
      : `${getEquipmentResourceUrl(resource)}/${params.id}`;

    // Check if we have file uploads
    const hasFiles =
      params.data &&
      ((params.data.ownershipPermitFiles && params.data.ownershipPermitFiles.length > 0) ||
        (params.data.weightRatingFiles && params.data.weightRatingFiles.length > 0));

    try {
      const requestOptions: any = { method: 'PATCH' };

      if (hasFiles) {
        // Use FormData for file uploads
        const formData = new FormData();

        // Add all regular fields to FormData
        Object.keys(params.data).forEach(key => {
          if (key !== 'ownershipPermitFiles' && key !== 'weightRatingFiles') {
            formData.append(key, params.data[key]);
          }
        });

        // Add ownership permit files
        if (params.data.ownershipPermitFiles) {
          params.data.ownershipPermitFiles.forEach((file: File) => {
            formData.append('ownershipPermitFiles', file);
          });
        }

        // Add weight rating files
        if (params.data.weightRatingFiles) {
          params.data.weightRatingFiles.forEach((file: File) => {
            formData.append('weightRatingFiles', file);
          });
        }

        requestOptions.body = formData;
      } else {
        // Use JSON for regular data
        requestOptions.body = JSON.stringify(params.data);
      }

      const { json } = await httpClient(url, requestOptions);

      return { data: json };
    } catch (error) {
      console.error(`Error updating equipment ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(getEquipmentSubResourceUrl(resource, id), {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple equipment ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = getEquipmentSubResourceUrl(resource, params.id);

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });

      return { data: json };
    } catch (error) {
      console.error(`Error deleting equipment ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(getEquipmentSubResourceUrl(resource, id), {
            method: 'DELETE',
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple equipment ${resource}:`, error);
      throw error;
    }
  },
};
